import { createStore } from 'vuex';
import axios from 'axios'
import * as utils from './utils.js';

let scheduleRequestCounter = 0;
let scheduleAbortController = null;

const Url = () => {
    const fullUrl = window.location.href || "https://studyplanner.co.cit.tum.de";
    if (fullUrl.includes('/save/')) {
        return fullUrl.split('/save/');
    }
    else {
        return [fullUrl, null];
    }
};

const LOAD_SAVE = async (data) => {
    try {
        const response = await axios.post(
            process.env.VUE_APP_API_URL + '/api/decode',
            { data: data }
        );

        const decoded = response.data.decoded;
        return { decoded };

    } catch (error) {
        console.error('Error decoding data:', error);
        return null;
    }
};


const getDefaultState = () => {
    return {
        config: null,
        program: {
            degree: "",
            major: "",
            fpso: "",
            standardPeriodOfStudy: 0,
            maxPeriodOfStudy: 0,
            thesisModuleId: "",
            germanRequired: true,
            englishRequired: true,
            blocks: [],
            structures: [],
            modules: [],
            offeredSemesters: {},
            moduleOccurrences: {}
        },
        preferences: {
            id: "",
            degree: "",
            major: "",
            startSemester: "",
            fpso: "",
            structures: {},
            currentSemester: null,
            passedModules: [],
            undesiredModules: [],
            desiredModules: [],
            desiredInSemModules: {},
            undesiredInSemModules: {},
            en: true,
            de: true,
            minEctsPerSemester: 0,
            maxEctsPerSemester: 32,
            minExamsPerSemester: 0,
            maxExamsPerSemester: 6,
            ignoreTimetable: false,
            semesterPreferences: {},
            previousSchedule: {},
            customSessions: [],
            saveDetails: {
                date: null,
                version: null,
                savedSemester: null,
                fromSave: false,
                taken: [],
                start: true,
                encodedSaveData: null,
            },
        },
        schedule: {},
        offeredSemesters: {},
        blockCredits: {},
        nextSemesters: [],
        violatedConstraints: [],
        isLoadingSchedule: false,
        isLoadingProgram: false,
        hasSolution: false,
        programLoaded: false,
        currentModal: null,
        currentModalSemester: null,
        openSettingsInModal: false,
        promptedPassedModules: false,
        expectedPassedModulesRequired: [],
        expectedPassedModulesRecommended: [],
        errorOccurred: false,
        history: [], // Stack to store the history of state changes
    }
}

const getDefaultSemesterPreference = () => {
    return {
        default: true,
        minEcts: 0,
        maxEcts: 32,
        minExams: 0,
        maxExams: 6,
        freeMon: false,
        freeTue: false,
        freeWed: false,
        freeThu: false,
        freeFri: false,
    }
}

const getDefaultValue = (property, subproperty = null) => {
    if (subproperty) {
        return getDefaultState()[property][subproperty];
    }
    return getDefaultState()[property];
}

export default createStore({
    state: getDefaultState(),
    getters: {
        frontendCurrentSemester(state) {
            if (!state.config || !state.config.currentSemester) return null;
            let currentSem = state.config.currentSemester;
            let startSem = state.preferences.startSemester;
            if (currentSem && startSem) {
                let type = currentSem[0];
                let year = parseInt(currentSem.slice(1));
                let upcomingSem = type === 's' ? 'w' + year : 's' + (year + 1).toString().padStart(2, '0');
                if (startSem === upcomingSem) {
                    return upcomingSem;
                }
            }
            return currentSem;
        },
        reqPrefsSet(state, getters) {
            const basicConditionsMet = getters.programPrefsSet
                && state.preferences.startSemester.length !== 0;

            const allStructuresHavePreference = state.program.structures.every(structure => {
                const structurePref = state.preferences.structures[structure.id];
                return structurePref !== undefined && structurePref !== "";
            });

            return basicConditionsMet && allStructuresHavePreference;
        },
        EncodedSaveData(state) {
            return state.preferences.saveDetails.encodedSaveData;
        },
        programPrefsSet(state) {
            return state.preferences.degree.length !== 0
                && state.preferences.major.length !== 0
                && state.preferences.fpso.length !== 0;
        },
        requiredModules(state) {
            const required = state.program.blocks.filter(block => block.allRequired)
                .map(block => block.modules)
                .flat();

            let strucRequired = [];

            state.program.structures.forEach(struc => {
                const selectedElementId = state.preferences.structures?.[struc.id];
                const selectedElement = struc.elements.find(element => element.id === selectedElementId);

                if (selectedElement) {
                    selectedElement.blocks.forEach(block => {
                        if (block.allRequired) {
                            strucRequired = [...strucRequired, ...block.modules];
                        }
                    });
                }
            });
            return [...required, ...strucRequired];
        },
        desiredModules(state) {
            return state.preferences.desiredModules;
        },
        mockModules(state) {
            const mock = state.program.blocks
                .map(block => block.modules)
                .flat()
                .filter(module => module.includes('mock'));  // get modules with 'mock' in their id

            return [...mock];
        },
        addedPassedModules(state) {
            return (state.preferences.passedModules.length !== 0 || state.promptedPassedModules) || state.preferences.fromStorage;
        },
        prefChangeDisabled(state) {
            return state.isLoadingSchedule;
        },
        totalECTS(state) {
            return Object.values(state.schedule)
                .map(item => item.ects)
                .reduce((accumulator, currentValue) => accumulator + currentValue, 0);
        },
        saveStart(state) {
            return state.preferences.saveDetails.fromSave && state.preferences.saveDetails.start;
        }
    },
    mutations: {
        SET_INITIAL_PREFERENCES(state, { preferences }) {
            state.preferences = preferences;
        },
        REDIRECT_TO_HOME() {
            console.log("Redirecting to home...");
            window.location.replace(Url()[0]);
        },
        UPDATE_ENCODED_SAVE_DATA(state, { encoding }) {
            state.preferences.saveDetails.encodedSaveData = encoding;

        },
        SET_CONFIG(state, config) {
            state.config = config;
        },
        RESET_STATE(state) {
            Object.assign(state, getDefaultState());
        },
        RESET_PROPERTY(state, property) {
            state[property] = getDefaultValue(property);
            if (property === "preferences" && state.programLoaded) {
                for (let i = 1; i <= state.program.maxPeriodOfStudy; i++) {
                    this.commit("ADD_SEMESTER_PREFERENCE", {
                        semester: i,
                        semesterPreference: getDefaultSemesterPreference()
                    });
                }
            }
        },
        RESET_SUBPROPERTY(state, { property, subproperty }) {
            state[property][subproperty] = getDefaultValue(property, subproperty);
        },
        SET_PROGRAM(state, program) {
            state.program = program;
        },
        SET_PREFERENCE(state, preference) {
            state.preferences[preference.key] = preference.value;
        },
        SET_STRUCTURE_OPTION(state, { structureId, option }) {
            state.preferences.structures = { ...state.preferences.structures, [structureId]: option };
        },
        ADD_PASSED_MODULE(state, { passedModule, bulk = false }) {
            if (!state.preferences.passedModules.includes(passedModule)) {
                // make sure to keep module in only one choice-list
                this.commit("REMOVE_UNDESIRED_MODULE", { undesiredModule: passedModule, removeSameNameMocks: false, bulk: true });
                this.commit("REMOVE_DESIRED_MODULE", { desiredModule: passedModule, bulk: true });
                this.commit("REMOVE_DESIRED_IN_SEM_MODULE", { desiredInSemModule: passedModule, bulk: true });
                state.preferences.passedModules.push(passedModule);
            }
            if (!bulk) {
                this.commit('SAVE_STATE');
            }
        },
        REMOVE_PASSED_MODULE(state, { passedModule, bulk = false }) {
            // Remove module from passed modules
            state.preferences.passedModules = state.preferences.passedModules.filter(num => num !== passedModule)

            // Check if there's any module with the same name in undesiredModules
            const module = state.program.modules.find(module => module.id === passedModule);
            if (!module || !module.mock) return;
            const sameMockUndesired = state.program.modules.find(module => {
                return module.mock && module.name === module.name && state.preferences.undesiredModules.includes(module.id);
            });

            // Add module to undesiredModules, if other mock of same name is
            if (sameMockUndesired !== undefined) {
                this.commit("ADD_UNDESIRED_MODULE", { undesiredModule: passedModule, bulk: true });
            }
            if (!bulk) {
                this.commit('SAVE_STATE');
            }
        },
        ADD_UNDESIRED_MODULE(state, { undesiredModule, bulk = false }) {
            const foundModule = state.program.modules.find(module => module.id === undesiredModule);
            let modulesToAdd = [];

            // Gather modules to add
            if (foundModule && foundModule.mock && !foundModule.id.includes("UEG")) {
                modulesToAdd = state.program.modules
                    .filter(module => module.name === foundModule.name && module.mock)
                    .map(module => module.id);
            } else {
                modulesToAdd = [undesiredModule];
            }

            // Create a new reference for undesiredModules to ensure reactivity
            const currentUndesiredModules = [...state.preferences.undesiredModules];

            modulesToAdd.forEach(moduleId => {
                if (!currentUndesiredModules.includes(moduleId) &&
                    !state.preferences.passedModules.includes(moduleId)) {
                    this.commit("REMOVE_DESIRED_MODULE", { desiredModule: moduleId, bulk: true });
                    this.commit("REMOVE_DESIRED_IN_SEM_MODULE", { desiredInSemModule: moduleId, bulk: true });
                    currentUndesiredModules.push(moduleId); // Add to temporary array
                }
            });

            // Update state with new reference
            state.preferences.undesiredModules = currentUndesiredModules;
            if (!bulk) {
                this.commit('SAVE_STATE');
            }
        },
        REMOVE_UNDESIRED_MODULE(state, payload) {
            let undesiredModule, removeSameNameMocks, bulk;

            // Check if payload is an object (new usage)
            if (typeof payload === 'object' && payload !== null) {
                undesiredModule = payload.undesiredModule;
                removeSameNameMocks = payload.removeSameNameMocks;
                bulk = payload.bulk || false; // Ensure bulk is defined, default to false
                // This is optional
            } else {
                // For backward compatibility (existing usage)
                undesiredModule = payload;
                removeSameNameMocks = true;
                bulk = false; // Default to false if not provided
            }

            const foundModule = state.program.modules.find(module => module.id === undesiredModule);
            let modulesToRemove = new Set();

            // last condition is a hack to avoid no plan if one UEG module is undesired
            if (removeSameNameMocks && foundModule && foundModule.mock && !foundModule.id.includes("UEG")) {
                // If the module is a mock, gather all modules with the same name
                state.program.modules
                    .filter(module => module.name === foundModule.name && module.mock)
                    .forEach(module => modulesToRemove.add(module.id));
            } else {
                // For non-mock modules, remove only the specified module
                modulesToRemove.add(undesiredModule);
            }

            // Remove modules from undesiredModules
            state.preferences.undesiredModules = state.preferences.undesiredModules
                .filter(moduleId => !modulesToRemove.has(moduleId));

            if (!bulk) {
                this.commit('SAVE_STATE');
            }
        },
        ADD_DESIRED_MODULE(state, { desiredModule, bulk = false }) {
            if (!state.preferences.desiredModules.includes(desiredModule)) {
                this.commit("REMOVE_UNDESIRED_MODULE", { undesiredModule: desiredModule, removeSameNameMocks: false, bulk: true });
                state.preferences.desiredModules.push(desiredModule);
                if (!bulk) {
                    this.commit('SAVE_STATE');
                }
            }
        },
        REMOVE_DESIRED_MODULE(state, { desiredModule, bulk = false }) {
            if (state.preferences.desiredModules.includes(desiredModule)) {
                state.preferences.desiredModules = state.preferences.desiredModules.filter(item => item !== desiredModule);
                if (!bulk) {
                    this.commit('SAVE_STATE');
                }
            }

        },
        ADD_DESIRED_IN_SEM_MODULE(state, { module, semester, bulk = false }) {
            if (!state.preferences.passedModules.includes(module)) {
                // make sure to keep module in only one choice-list
                this.commit("REMOVE_PASSED_MODULE", { passedModule: module, bulk: true });
                this.commit("REMOVE_UNDESIRED_MODULE", { undesiredModule: module, removeSameNameMocks: false, bulk: true });
                this.commit("REMOVE_DESIRED_MODULE", { desiredModule: module, bulk: true });
                // avoid double assignment
                this.commit("REMOVE_DESIRED_IN_SEM_MODULE", { desiredInSemModule: module, bulk: true });
                this.commit("REMOVE_UNDESIRED_IN_SEM_MODULE", { undesiredInSemModule: module, bulk: true });
                state.preferences.desiredInSemModules[module] = semester;
                if (!bulk) { this.commit('SAVE_STATE'); }

            }
        },
        REMOVE_DESIRED_IN_SEM_MODULE(state, { desiredInSemModule, bulk = false }) {
            if (state.preferences.desiredInSemModules[desiredInSemModule] !== undefined) {
                delete state.preferences.desiredInSemModules[desiredInSemModule];
                if (!bulk) {
                    this.commit("SAVE_STATE");
                }
            }
        },
        ADD_UNDESIRED_IN_SEM_MODULE(state, { module, semester, bulk = false }) {
            if (!state.preferences.passedModules.includes(module)) {
                this.commit("REMOVE_PASSED_MODULE", { passedModule: module, bulk: true });
                this.commit("REMOVE_DESIRED_MODULE", { desiredModule: module, bulk: true });
                this.commit("REMOVE_DESIRED_IN_SEM_MODULE", { desiredInSemModule: module, bulk: true });
                this.commit("REMOVE_UNDESIRED_IN_SEM_MODULE", { undesiredInSemModule: module, bulk: true });

                state.preferences.undesiredInSemModules[module] = semester;
                if (!bulk) { this.commit('SAVE_STATE'); }
            }
        },
        REMOVE_UNDESIRED_IN_SEM_MODULE(state, { undesiredInSemModule, bulk = false }) {
            if (state.preferences.undesiredInSemModules[undesiredInSemModule] !== undefined) {
                delete state.preferences.undesiredInSemModules[undesiredInSemModule];
                if (!bulk) {
                    this.commit("SAVE_STATE");
                }
            }
        },
        ADD_CUSTOM_SESSION(state, { session, bulk = false }) {
            state.preferences.customSessions.push(session);
            if (!bulk) this.commit('SAVE_STATE');
        },
        UPDATE_CUSTOM_SESSION(state, { session, bulk = false }) {
            const index = state.preferences.customSessions.findIndex(s => s.id === session.id);
            if (index !== -1) {
                state.preferences.customSessions.splice(index, 1, session);
                if (!bulk) this.commit('SAVE_STATE');
            }
        },
        REMOVE_CUSTOM_SESSION(state, { sessionId, bulk = false }) {
            state.preferences.customSessions = state.preferences.customSessions.filter(s => s.id !== sessionId);
            if (!bulk) this.commit('SAVE_STATE');
        },
        ADD_NEW_STRUCTURE(state, { payload }) {
            if (!payload || typeof payload !== "object") {
                console.error("Payload is not valid:", payload);
                return;
            }

            if (!Array.isArray(state.program.structures)) {
                state.structures = [];
            }

            state.program.structures.unshift(payload);

        },
        UPDATE_SEMESTER_PREFERENCE(state, { semester, key, value, bulk = false }) {
            if (!state.preferences.semesterPreferences[semester]) {
                state.preferences.semesterPreferences[semester] = { default: false };
            }
            state.preferences.semesterPreferences[semester][key] = value;
            if (!bulk) this.commit('SAVE_STATE');
        },
        ADD_SEMESTER_PREFERENCE(state, { semester, semesterPreference }) {
            state.preferences.semesterPreferences[semester] = semesterPreference;
        },
        UPDATE_SCHEDULE(state, schedule) {
            state.schedule = schedule;
            state.preferences.previousSchedule = state.schedule;
        },
        UPDATE_OFFERED_SEMESTERS(state, offeredSemesters) {
            state.offeredSemesters = offeredSemesters;
        },
        UPDATE_BLOCK_CREDITS(state, blockCredits) {
            state.blockCredits = blockCredits;
        },
        SET_SCHEDULE_LOADING_STATUS(state, status) {
            state.isLoadingSchedule = status;
        },
        SET_PROGRAM_LOADING_STATUS(state, status) {
            state.isLoadingProgram = status;
        },
        SET_SOLUTION_STATUS(state, status) {
            state.hasSolution = status;
        },
        SET_PROGRAM_LOAD_STATUS(state, status) {
            state.programLoaded = status;
        },
        SET_ERROR_STATUS(state, status) {
            state.errorOccurred = status;
        },
        SET_VIOLATED_CONSTRAINTS(state, constraints) {
            state.violatedConstraints = constraints;
        },
        OPEN_MODAL(state, payload) {
            // Check if payload is an object
            if (typeof payload === 'object' && payload !== null) {
                // For semester-dependent modals
                state.currentModal = payload.modal;
                state.currentModalSemester = payload.semester;
            } else {
                // For semester-independent modals
                state.currentModal = payload;
            }
        },
        CLOSE_MODAL(state) {
            state.currentModal = null;
            state.currentModalSemester = null;
        },
        SET_OPEN_SETTINGS_IN_MODAL(state, value) {
            state.openSettingsInModal = value;
        },
        RESET_PROGRAM(state) {
            state.programLoaded = false;
            state.program = getDefaultValue("program");
        },
        RESET_ALL_PREFERENCES_BUT_BASE(state) {
            state.promptedPassedModules = false;
            let preferences = getDefaultState().preferences;
            preferences.degree = state.preferences.degree;
            preferences.major = state.preferences.major;
            preferences.startSemester = state.preferences.startSemester;
            preferences.fpso = state.preferences.fpso;
            state.preferences = preferences;
        },
        RESET_SCHEDULE(state) {
            state.hasSolution = false;
            state.schedule = getDefaultValue("schedule");
            state.offeredSemesters = getDefaultValue("offeredSemesters");
        },
        RESET_STRUCTURES(state) {
            state.preferences.structures = {};
        },
        RESET_PASSED_MODULES(state) {
            state.preferences.passedModules = [];
        },
        RESET_DESIRED_MODULES(state) {
            state.preferences.desiredModules = [];
            state.preferences.desiredInSemModules = {};
        },
        RESET_UNDESIRED_MODULES(state) {
            state.preferences.undesiredModules = [];
            state.preferences.undesiredInSemModules = {};
        },
        SET_PROMPTED_PASSED_MODULES(state) {
            state.promptedPassedModules = true;
        },
        SET_EXPECTED_PASSED_MODULES_REQUIRED(state, modules) {
            state.expectedPassedModulesRequired = modules;
        },
        SET_EXPECTED_PASSED_MODULES_RECOMMENDED(state, modules) {
            state.expectedPassedModulesRecommended = modules;
        },
        SET_NEXT_SEMESTERS(state, semesters) {
            state.nextSemesters = semesters;
        },
        SET_MAX_ECTS_PER_SEMESTER(state, maxEcts) {
            state.preferences.maxEctsPerSemester = maxEcts;
        },
        SAVE_STATE(state) {
            if (!state.history) {
                state.history = [];
            }

            // Only save the relevant parts of state that affect scheduling
            const snapshot = {
                desiredModules: [...state.preferences.desiredModules],
                desiredInSemModules: { ...state.preferences.desiredInSemModules },
                undesiredModules: [...state.preferences.undesiredModules],
                undesiredInSemModules: { ...state.preferences.undesiredInSemModules },
                customSessions: state.preferences.customSessions ? JSON.parse(JSON.stringify(state.preferences.customSessions)) : [],
                semesterPreferences: state.preferences.semesterPreferences ? JSON.parse(JSON.stringify(state.preferences.semesterPreferences)) : {},
                timestamp: Date.now()
            };

            state.history.push(snapshot);
            // Limit history size to prevent memory issues
            if (state.history.length > 30) {
                state.history.shift();
            }
        },
        UNDO_CHANGE(state) {
            if (state.history && state.history.length > 1) {

                state.history.pop();
                const previousState = state.history[state.history.length - 1];

                // Only restore the relevant parts
                state.preferences.desiredModules = [...previousState.desiredModules];
                state.preferences.desiredInSemModules = { ...previousState.desiredInSemModules };
                state.preferences.undesiredModules = [...previousState.undesiredModules];
                state.preferences.undesiredInSemModules = { ...previousState.undesiredInSemModules };
                if (previousState.customSessions) {
                    state.preferences.customSessions = JSON.parse(JSON.stringify(previousState.customSessions));
                }
                if (previousState.semesterPreferences) {
                    state.preferences.semesterPreferences = JSON.parse(JSON.stringify(previousState.semesterPreferences));
                }

                // Clear other related state if needed
                state.currentModal = null;
                state.currentModalSemester = null;

            } else { console.warn("Undo not possible: only one state in history") }
        },

        SET_START(state, bool) {
            state.preferences.saveDetails.start = bool;
        }
    },
    actions: {
        async initializeAppState({ commit }) {

            const urlParam = Url()?.[1];

            if (urlParam) {
                const savedData = await LOAD_SAVE(urlParam);

                if (savedData && savedData.decoded) {
                    savedData.decoded.saveDetails.fromSave = true;

                    commit("SET_INITIAL_PREFERENCES", {
                        preferences: savedData.decoded,
                    });
                    await this.dispatch("loadConfig");
                    await this.dispatch("requestProgramDetails", { preservePreferences: true });

                    const savedSemester = this.state.preferences.saveDetails.savedSemester;
                    const isSameSemester = this.getters.frontendCurrentSemester === savedSemester
                        || (this.state.config && this.state.config.currentSemester === savedSemester);
                    if (isSameSemester) {
                        await this.dispatch("requestSchedule");
                    }
                    this.state.promptedPassedModules = true;
                    this.dispatch("showSaveModal");
                    commit("SAVE_STATE");

                }

            }


        },
        async loadConfig({ commit }) {
            try {
                const response = await fetch(process.env.VUE_APP_API_URL + '/api/config');
                if (!response.ok) {
                    throw new Error('Failed to fetch config');
                }
                const config = await response.json();
                commit("SET_CONFIG", config);
            } catch (error) {
                console.error("Failed to load config", error);
            }
        },
        decodeSaveData(encodedData) {
            return axios.post(process.env.VUE_APP_API_URL + '/api/decode', encodedData)
                .then(response => {
                    return response.data;
                })
                .catch(error => {
                    console.error('Error decoding data:', error);
                    return null;
                });
        },

        async requestSchedule(context) {
            context.commit("SET_SCHEDULE_LOADING_STATUS", true);

            if (scheduleAbortController) {
                try {
                    scheduleAbortController.abort();
                } catch (e) {
                    console.error("Error aborting previous schedule request:", e);
                }
            }
            scheduleAbortController = new AbortController();
            const currentRequestId = ++scheduleRequestCounter;

            try {
                if (context.state.config === null || context.state.program === null) {
                    await context.dispatch("loadConfig");
                    await context.dispatch("requestProgramDetails");
                }

                if (currentRequestId !== scheduleRequestCounter) {
                    console.log("Request ID doesn't match");
                    return false;
                }

                context.state.preferences.currentSemester = context.getters.frontendCurrentSemester;

                const response = await axios.post(
                    process.env.VUE_APP_API_URL + "/api/schedule",
                    { preferences: context.state.preferences },
                    { signal: scheduleAbortController.signal }
                );

                if (currentRequestId !== scheduleRequestCounter) {
                    console.log("Request ID doesn't match");
                    return false;
                }

                if (response.status === 200) {
                    if (Array.isArray(response.data.violated_constraints) &&
                        response.data.violated_constraints.length > 0) {
                        context.commit("SET_SOLUTION_STATUS", false);
                        context.commit("SET_VIOLATED_CONSTRAINTS", response.data.violated_constraints);
                    } else {
                        const { schedule, blockCredits, offeredSemesters } = response.data;
                        if (schedule) {
                            context.commit("SET_SOLUTION_STATUS", true);
                            context.commit("UPDATE_SCHEDULE", schedule);
                            context.commit("UPDATE_OFFERED_SEMESTERS", offeredSemesters);
                            context.commit("UPDATE_BLOCK_CREDITS", blockCredits);
                            context.commit("SET_VIOLATED_CONSTRAINTS", []);
                        }
                    }
                } else if (response.status === 400) {
                    context.commit("SET_SOLUTION_STATUS", false);
                    context.commit("SET_VIOLATED_CONSTRAINTS", []);
                } else {
                    context.commit("SET_SOLUTION_STATUS", false);
                }

                return true;

            } catch (error) {
                if (axios.isCancel(error) || error.name === 'CanceledError' || error.name === 'AbortError') {
                    console.error("Schedule request canceled", error);
                    return false;
                }
                if (currentRequestId !== scheduleRequestCounter) {
                    console.error("Schedule request counter mismatch", error);
                    return false;
                }
                context.commit("SET_ERROR_STATUS", true);
                console.error(error);
                return false;
            } finally {
                if (currentRequestId === scheduleRequestCounter) {
                    context.commit("SET_SCHEDULE_LOADING_STATUS", false);
                }
            }
        },
        async requestProgramDetails(context, payload = {}) {
            const preservePreferences = payload.preservePreferences || false;

            this.commit("SET_PROGRAM_LOADING_STATUS", true);
            if (context.state.config === null) {
                console.error("Config not loaded yet.");
                await context.dispatch("loadConfig");
            }
            if (!context.state.config || !context.state.config.degrees) {
                console.error("Config or degrees failed to load.");
                context.commit("SET_PROGRAM_LOADING_STATUS", false);
                return false;
            }
            const degree = context.state.config.degrees.find(degree => degree.id === context.state.preferences.degree);
            if (!degree) {
                console.error("Degree not found for", context.state.preferences.degree);
                context.commit("SET_PROGRAM_LOADING_STATUS", false);
                return false;
            }
            const major = degree.departments.flatMap(department => department.majors).find(major => major.name === context.state.preferences.major);
            if (!major) {
                console.error("Major not found for", context.state.preferences.major);
                context.commit("SET_PROGRAM_LOADING_STATUS", false);
                return false;
            }
            context.state.preferences.id = `${context.state.preferences.degree}_${major.id}_${context.state.preferences.fpso}`;
            return axios.get(process.env.VUE_APP_API_URL + "/api/program", {
                params: {
                    id: context.state.preferences.id
                }
            })
                .then(response => {
                    context.commit("SET_ERROR_STATUS", false);
                    context.commit("SET_PROGRAM", response.data);
                    // Set maxEctsPerSemester from program data if available, only if not preserving preferences
                    if (response.data.maxEctsPerSemester && !preservePreferences) {
                        context.commit("SET_MAX_ECTS_PER_SEMESTER", response.data.maxEctsPerSemester);
                    }

                    if (!preservePreferences) {
                        // reset semesterPreferences if set from previous request
                        context.commit("RESET_SUBPROPERTY", { property: "preferences", subproperty: "semesterPreferences" });
                    }

                    // dynamically add elements for semesterPreferences for each semester
                    // Check if empty OR if we need to fill gaps (if preserved but partial)
                    if (utils.isEmpty(context.state.preferences.semesterPreferences)) {
                        for (let i = 1; i <= context.state.program.maxPeriodOfStudy; i++) {
                            this.commit("ADD_SEMESTER_PREFERENCE", {
                                semester: i,
                                semesterPreference: getDefaultSemesterPreference()
                            });
                        }
                    } else if (preservePreferences) {
                        // Even if preserving, ensure all semesters exist up to maxPeriodOfStudy
                        for (let i = 1; i <= context.state.program.maxPeriodOfStudy; i++) {
                            if (!context.state.preferences.semesterPreferences[i]) {
                                this.commit("ADD_SEMESTER_PREFERENCE", {
                                    semester: i,
                                    semesterPreference: getDefaultSemesterPreference()
                                });
                            }
                        }
                    }
                    context.commit("SET_PROGRAM_LOAD_STATUS", true);
                })
                .catch(error => {
                    context.commit("SET_ERROR_STATUS", true);
                    context.commit("RESET_PROGRAM");
                    console.error(error);
                })
                .finally(() => {
                    context.commit("SET_PROGRAM_LOADING_STATUS", false);
                });
        },
        async sendReport(context, content) {
            let message = utils.sanitizeInput(content.message);

            const payload =
            {
                "email": content.email ? utils.sanitizeInput(content.email) : "",
                "message": message,
                "save": "",
                "source": process.env.VUE_APP_API_URL,
                "preferences": content.consentData ? context.state.preferences : {},
                "browser": content.consentMetaData ? utils.getBrowserInfo() : {}
            };
            if (content.consentData) {
                try {
                    const response = await axios.post(process.env.VUE_APP_API_URL + "/api/encode", context.state.preferences);
                    const url = window.location.origin + "/save/" + response.data['encoded'];
                    payload.save = url;
                } catch (error) {
                    console.error("Failed to encode preferences for report:", error);
                }
            }
            try {
                const response = await axios.post(process.env.VUE_APP_API_URL + "/api/report", payload);
                return response.status >= 200 && response.status < 300;
            } catch (error) {
                console.error(error);
                return false;
            }
        },
        openModal({ commit }, payload) {
            commit("OPEN_MODAL", payload);
        },
        showSaveModal(context) {
            const saveBegin = context.state.preferences.saveDetails.fromSave;
            if (!saveBegin) return;

            const savedSemester = context.state.preferences.saveDetails.savedSemester;
            const sameSemester = context.state.preferences.currentSemester === savedSemester
                || (context.state.config && context.state.config.currentSemester === savedSemester);

            if (!sameSemester) {
                context.commit("OPEN_MODAL", "SaveSemesterChangedModal");

            } else if (!context.state.hasSolution && context.state.violatedConstraints[0] === "Could not create the same schedule as previous one.") {
                context.commit("OPEN_MODAL", "SaveUnvalidModal");
            }
            context.commit("SET_START", false);
        },
        selectStructureOption({ commit }, payload) {
            commit("SET_STRUCTURE_OPTION", payload);
        },
        async requestExpectedPassedModules(context) {
            try {
                const response = await axios.post(
                    process.env.VUE_APP_API_URL + "/api/expected_passed_modules",
                    { preferences: context.state.preferences }
                );

                if (response.status === 200) {
                    const data = response.data;
                    const required = data.requiredPassedModules || [];
                    const recommended = data.recommendedPassedModules || [];

                    return { required, recommended };
                }
            } catch (error) {
                console.error("Failed to fetch expected passed modules:", error);
                return { required: [], recommended: [] };
            }
        }
    },
});
